/**
 * Light, dark, or whatever the machine is set to.
 *
 * Three states rather than two, but the default is LIGHT rather than "follow
 * the system". The portal is a work surface that shows client-facing artwork,
 * proposal previews and portfolio imagery, and those are all designed, judged
 * and printed against white. Handing someone a dark shell because their laptop
 * happens to be dark means the thing they are evaluating sits in a frame that
 * changes how it reads — and nobody chose that, the operating system did.
 *
 * "Match system" is still there for anyone who wants it; it is a choice now
 * instead of an assumption.
 *
 * An explicit choice stamps data-theme on <html>, which beats the media query
 * in both directions. Choosing "system" removes the attribute again so the
 * media query is back in charge — it does not freeze whatever the system
 * happened to be at that moment.
 */

// Two themes. "Match system" was dropped: an explicit Light or Dark is always
// stamped on <html>, so the operating system never decides for you.
export const THEMES = ['light', 'dark']
export const STORAGE_KEY = 'ac_theme'

// What someone sees before they have ever expressed a preference.
export const DEFAULT_THEME = 'light'

export function readTheme(storage = safeStorage()) {
  try {
    const saved = storage?.getItem(STORAGE_KEY)
    return THEMES.includes(saved) ? saved : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

export function writeTheme(theme, storage = safeStorage()) {
  if (!THEMES.includes(theme)) return false
  try {
    storage?.setItem(STORAGE_KEY, theme)
    return true
  } catch {
    return false
  }
}

/**
 * Put the choice on the document.
 *
 * Returns what was applied, so a caller can tell "system" from the theme the
 * system currently resolves to.
 */
export function applyTheme(theme, root = document?.documentElement) {
  const next = THEMES.includes(theme) ? theme : DEFAULT_THEME
  if (!root) return next
  root.setAttribute('data-theme', next)
  return next
}

/** What the viewer is actually looking at, once "system" is resolved. */
export function resolveTheme(theme, prefersDark = systemPrefersDark()) {
  return theme === 'dark' ? 'dark' : 'light'
}

/** The next theme in the cycle, for a single control that steps through. */
export function nextTheme(theme) {
  const i = THEMES.indexOf(theme)
  return THEMES[(i < 0 ? 0 : i + 1) % THEMES.length]
}

export function systemPrefersDark() {
  try {
    return !!window.matchMedia?.('(prefers-color-scheme: dark)')?.matches
  } catch {
    return false
  }
}

function safeStorage() {
  try { return window.localStorage } catch { return null }
}
